import React, { FC } from 'react'
import GlobalContainer from '../globalContainer'

//* Images
import { ASSETS } from '../../../constants/assets'
import { ComponentHero } from '../../../models/components/hero'

//* Styles
import { LocaleCodes } from '../../../constants/localization.constants'
import { HERO_USECASES, PAGE_TYPES } from '../../../constants/useCases.constants'
import { TopicBreadcrumb } from '../../../models/topics/breadcrumb'
import { HeroOuter, StyledBreadcrumbs } from './style'
import { HeroGrid } from './subcomponent'
import { getBackgroundProp } from './util'

type Props = {
  data: ComponentHero
  isHomePage?: boolean
  showBreadcrumbs?: boolean
  pageTitle?: string
  pageUseCase?: string
  pageType?: string
  metaInformation?: any
  breadcrumb?: TopicBreadcrumb
  locale?: string
  location?: any
}

const Hero: FC<Props> = ({
  data,
  isHomePage = false,
  showBreadcrumbs,
  pageTitle = '',
  pageUseCase = '',
  pageType = '',
  metaInformation,
  breadcrumb,
  locale = LocaleCodes.ENGLISH.code,
  location,
}) => {
  if (!data) return null
  const locationData = location?.pathname || '/'

  const heroData: any = {
    eyebrow: '',
    headline: data.headline || '',
    theme: data.heroTheme || data.theme || '',
    subheadline: '',
    cta: [],
    heroTextHighlight: data.heroTextHighlight || false,
    useCase: data.useCase || HERO_USECASES.MINIMAL,
    embedVideo: data.embedVideo || false,
    logo: data.logo,
    subheadlineSmall: data.subheadlineSmall,
  }

  const backgroundVideo = data.backgroundVideo?.animationFile || null
  let backgroundVideoUrl = ''
  let backgroundAsset: any = data.backgroundAsset || null

  let imageDimensions: { width: number; height: number } | undefined
  let focalPoint: { x: number; y: number } | undefined
  let imageSrc: string = ''
  let altText: string = ''

  const backgroundVideoPages = [PAGE_TYPES.FLEX, PAGE_TYPES.EVENTS, PAGE_TYPES.RAWHTML]
  if (backgroundVideoPages.includes(pageType) && backgroundVideo?.file?.url) {
    backgroundVideoUrl = backgroundVideo.file.url
  }

  //* handles cases where we have a background image and no use case selected - HomepageHero & More
  if (heroData.useCase === HERO_USECASES.MINIMAL && backgroundAsset) {
    heroData.useCase = HERO_USECASES.PRODUCT_HERO
  }

  if (
    heroData.useCase === (HERO_USECASES.PRODUCT_HERO && backgroundAsset) ||
    (HERO_USECASES.FULL_WIDTH_HERO && backgroundAsset)
  ) {
    focalPoint = backgroundAsset.focalPoint?.focalPoint || null
    altText = backgroundAsset.altText || ''
    imageDimensions = backgroundAsset.image?.file?.details?.image || null
    imageSrc = backgroundAsset.image?.file?.url || ''
  } else if (pageType === PAGE_TYPES.FORM_FLEX && !backgroundAsset) {
    imageSrc = ASSETS.HERO_FORM_FLEX_BACKGROUND
  }

  if (heroData.useCase === HERO_USECASES.CENTERED_WITH_DOTS) {
    imageSrc = ASSETS.CENTER_TEXT_WITH_DOTS
  }

  if (
    heroData.useCase === HERO_USECASES.MINIMAL &&
    pageType !== PAGE_TYPES.EVENTS &&
    pageType !== PAGE_TYPES.RAWHTML
  ) {
    heroData.theme = 'light'
  }

  if (heroData.useCase === HERO_USECASES.MINIMAL && pageType === PAGE_TYPES.WEBINAR_RECORDING) {
    heroData.theme = 'dark'
  }

  //* enable subheadline for use cases that have it
  const pages = [
    PAGE_TYPES.SOLUTIONS,
    PAGE_TYPES.ABOUT_RPA,
    PAGE_TYPES.PARTNERS,
    PAGE_TYPES.FLEX,
    PAGE_TYPES.THANK_YOU,
    PAGE_TYPES.COMPANY_LISTING,
    PAGE_TYPES.RESOURCE_LISTING,
    PAGE_TYPES.COMPANY_LISTING,
    PAGE_TYPES.LEGAL,
    PAGE_TYPES.FORM_FLEX,
    PAGE_TYPES.RAWHTML,
    PAGE_TYPES.EVENTS,
  ]

  if (
    heroData.useCase === HERO_USECASES.MINIMAL ||
    heroData.useCase === HERO_USECASES.FULL_WIDTH_HERO ||
    pages.includes(pageType) ||
    isHomePage
  ) {
    heroData.subheadline =
      typeof data.subheadline === 'string' ? data.subheadline : data.subheadline?.subheadline || ''
  }

  //* Set only product variables
  let backgroundProps = getBackgroundProp(
    heroData,
    data,
    imageSrc,
    focalPoint,
    backgroundVideoUrl,
    pageType,
    imageDimensions,
    isHomePage
  )

  // Solution Banner Gradient
  let solBannerGradient = ''
  if (
    heroData.useCase === HERO_USECASES.FULL_WIDTH_HERO &&
    (pageType === PAGE_TYPES.SOLUTIONS || pageType === PAGE_TYPES.FLEX)
  ) {
    solBannerGradient = heroData.theme === 'light' ? 'light-gradient' : 'dark-gradient'
  }
  return (
    <HeroOuter
      {...backgroundProps}
      $heroTheme={heroData.theme}
      $useCase={heroData.useCase}
      $pageType={pageType}
      $displayBackgroundVideo={!!backgroundVideoUrl ? true : false}
      $heroTextHighlight={heroData.heroTextHighlight}
      data-cy="Hero"
      data-cytype={pageType}
      className={heroData.useCase === HERO_USECASES.FULL_WIDTH_HERO ? 'Hero__FullWidth' : ''}>
      <GlobalContainer className={`Hero__Container ${solBannerGradient}`}>
        {backgroundVideoUrl && (
          <>
            <div className="Hero__Gradient"></div>
            <video
              muted={true}
              autoPlay={true}
              loop={true}
              playsInline={true}
              className="Hero__Video">
              <source src={backgroundVideoUrl} />
            </video>
          </>
        )}

        {showBreadcrumbs && (
          <StyledBreadcrumbs
            backgroundTheme={heroData.theme}
            currentPathname={locationData}
            pageName={pageTitle}
            pageType={pageType}
            locale={locale}
            breadcrumb={breadcrumb}
            useCase={heroData.useCase}
          />
        )}

        <HeroGrid
          heroData={heroData}
          pageUseCase={pageUseCase}
          imageSrc={imageSrc}
          altText={altText}
          isHomePage={isHomePage}
          pageType={pageType}
          metaInformation={metaInformation}
        />
      </GlobalContainer>
    </HeroOuter>
  )
}

export default Hero
